import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import MediaStage from './MediaStage';
import MediaThumbnailStrip from './MediaThumbnailStrip';
import { buildMediaScans } from './mediaScans';
import { useScanMediaCarouselController } from './useScanMediaCarouselController';
import './styles.scss';

function ScanMediaCarousel({
    scans,
    template,
    caseThumbnailUrl,
    showTips,
    setShowTips,
}) {
    const mediaScans = useMemo(
        () => buildMediaScans(scans, caseThumbnailUrl, template),
        [caseThumbnailUrl, scans, template],
    );
    const {
        haveAllMediaFailed,
        selectedIndex,
        setSelectedIndex,
        setVisibleThumbnailIds,
        thumbnailStates,
    } = useScanMediaCarouselController(mediaScans);
    const hasNoMediaSources = mediaScans.length === 0;

    if (hasNoMediaSources || haveAllMediaFailed) {
        const emptyState = hasNoMediaSources
            ? {
                body: 'This case does not have any scan-specific thumbnails available yet.',
                title: 'No Media Available',
            }
            : {
                body: 'The available media thumbnails could not be loaded. Try reopening the viewer, loading a different case, or refreshing the webpage.',
                title: 'Unable To Load Media',
            };

        return (
            <div className="media-container">
                <div className="media-empty-state">
                    <BrokenImageOutlinedIcon className="media-empty-state-icon" />
                    <h3>{emptyState.title}</h3>
                    <p>{emptyState.body}</p>
                </div>
            </div>
        );
    }

    const activeScan = mediaScans[selectedIndex] || mediaScans[0] || null;

    return (
        <div className="media-container">
            <MediaStage
                activeScan={activeScan}
                mediaScans={mediaScans}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                setShowTips={setShowTips}
                showTips={showTips}
                thumbnailStates={thumbnailStates}
            />
            <MediaThumbnailStrip
                mediaScans={mediaScans}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                setVisibleThumbnailIds={setVisibleThumbnailIds}
                thumbnailStates={thumbnailStates}
            />
        </div>
    );
}

ScanMediaCarousel.propTypes = {
    caseThumbnailUrl: PropTypes.string,
    setShowTips: PropTypes.func.isRequired,
    showTips: PropTypes.bool.isRequired,
    template: PropTypes.string,
    scans: PropTypes.arrayOf(
        PropTypes.shape({
            alternateName: PropTypes.string,
            dentalCaseId: PropTypes.string,
            displayIndex: PropTypes.number,
            displayLabel: PropTypes.string,
            id: PropTypes.string,
            label: PropTypes.string,
            thumbnailUrl: PropTypes.string,
        }),
    ).isRequired,
};

ScanMediaCarousel.defaultProps = {
    caseThumbnailUrl: '',
    template: '',
};

export default ScanMediaCarousel;
